import type { HidChannel } from '../HidChannel'
import type { HidDeviceFilter } from '../HidChannelDescriptor'
import { DeviceError, DeviceErrorCode } from '../../../errors/DeviceError'

/**
 * WebHID HID 通道实现
 * 运行在浏览器或 Electron Renderer 进程中
 */
export class WebHidChannel implements HidChannel {
  public nativeDevice: HIDDevice | null = null
  private reportListeners: Map<
    (reportId: number, data: DataView) => void,
    (event: HIDInputReportEvent) => void
  > = new Map()

  constructor(existingDevice?: HIDDevice) {
    if (existingDevice) {
      this.nativeDevice = existingDevice
    }
  }

  /**
   * 判断当前环境是否支持 WebHID
   */
  public static isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.hid
  }

  public get isConnected(): boolean {
    return this.nativeDevice?.opened ?? false
  }

  public get deviceInfo() {
    if (!this.nativeDevice) return null
    return {
      vendorId: this.nativeDevice.vendorId,
      productId: this.nativeDevice.productId,
      productName: this.nativeDevice.productName,
    }
  }

  /**
   * 扫描设备 (静态方法)
   */
  public static async scan(filters: HidDeviceFilter[]): Promise<HIDDevice[]> {
    if (!this.isSupported()) {
      throw new DeviceError(
        DeviceErrorCode.NOT_SUPPORTED,
        'WebHID API is not supported in this environment',
      )
    }

    try {
      const requestOptions: HIDDeviceRequestOptions = {
        filters: filters.map((f) => {
          const filter: any = {
            vendorId: f.vendorId,
          }
          if (f.productId !== undefined) filter.productId = f.productId
          if (f.usagePage !== undefined) filter.usagePage = f.usagePage
          if (f.usage !== undefined) filter.usage = f.usage
          return filter
        }),
      }

      return await navigator.hid.requestDevice(requestOptions)
    } catch (error) {
      console.error('Failed to scan devices:', error)
      // scan 失败通常不应抛出异常阻断流程，而是返回空数组
      // 但如果是用户取消选择，WebHID 会抛出 AbortError 或其他错误
      // 如果不是用户取消，可以记录日志。
      return []
    }
  }

  /**
   * 获取已连接设备
   */
  public static async getConnected(): Promise<HIDDevice[]> {
    if (!this.isSupported()) {
      return []
    }
    try {
      return await navigator.hid.getDevices()
    } catch (error) {
      console.error('Failed to get connected devices:', error)
      return []
    }
  }

  public async open(): Promise<void> {
    console.log('[WebHID] Opening device...')
    if (!this.nativeDevice) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, 'No nativeDevice selected.')
    }

    try {
      if (!this.nativeDevice.opened) {
        await this.nativeDevice.open()
        console.log(`[WebHID] Device ${this.nativeDevice.productName} opened.`)
      } else {
        console.log(`[WebHID] Device ${this.nativeDevice.productName} already opened, skipping.`)
      }
    } catch (error) {
      // 如果是因已打开导致的错误，也忽略（部分浏览器实现可能抛出异常）
      if (this.nativeDevice.opened) {
        console.warn('[WebHID] Device already opened, continue.')
      } else {
        console.error(
          `[WebHID] Failed to open nativeDevice ${this.nativeDevice.productName}:`,
          error,
        )
        throw new DeviceError(
          DeviceErrorCode.OPEN_FAILED,
          `Failed to open nativeDevice ${this.nativeDevice.productName}`,
          error,
        )
      }
    }
  }

  public async close(): Promise<void> {
    console.log('[WebHID] Closing device...')
    if (this.nativeDevice?.opened) {
      try {
        await this.nativeDevice.close()
      } catch (error) {
        console.warn('[WebHID] Failed to close nativeDevice:', error)
      }
    }
  }

  public async sendOutputReport(reportId: number, data: Uint8Array): Promise<void> {
    if (!this.nativeDevice || !this.nativeDevice.opened) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, 'Device is not connected.')
    }

    try {
      // 兼容性处理：WebHID sendReport 第二个参数需要 BufferSource
      // 某些浏览器可能对 Uint8Array 的支持有差异，强制转为 any 避免 TS 报错
      await this.nativeDevice.sendReport(reportId, data as any)
    } catch (error) {
      console.error('Failed to send report:', error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'Failed to send report', error)
    }
  }

  public async sendFeatureReport(reportId: number, data: Uint8Array): Promise<void> {
    if (!this.nativeDevice || !this.nativeDevice.opened) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, 'Device is not connected.')
    }

    try {
      await this.nativeDevice.sendFeatureReport(reportId, data as any)
    } catch (error) {
      console.error('Failed to send feature report:', error)
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, 'Failed to send feature report', error)
    }
  }

  public async getFeatureReport(reportId: number): Promise<DataView> {
    if (!this.nativeDevice || !this.nativeDevice.opened) {
      throw new DeviceError(DeviceErrorCode.NOT_CONNECTED, 'Device is not connected.')
    }

    try {
      return await this.nativeDevice.receiveFeatureReport(reportId)
    } catch (error) {
      console.error('Failed to receive feature report:', error)
      throw new DeviceError(DeviceErrorCode.READ_FAILED, 'Failed to receive feature report', error)
    }
  }

  public onInputReport(callback: (reportId: number, data: DataView) => void): void {
    if (!this.nativeDevice) return

    // 关键：注册前先移除同一回调，避免重复触发 inputreport。
    this.offInputReport(callback)

    const listener = (event: HIDInputReportEvent) => {
      // console.log(`[WebHID] Registering report listener for reportId=${event.reportId}`)
      callback(event.reportId, event.data)
    }

    this.reportListeners.set(callback, listener)
    this.nativeDevice.addEventListener('inputreport', listener)
  }

  public offInputReport(callback: (reportId: number, data: DataView) => void): void {
    if (!this.nativeDevice) return

    const listener = this.reportListeners.get(callback)
    if (listener) {
      this.nativeDevice.removeEventListener('inputreport', listener)
      this.reportListeners.delete(callback)
    }
  }
}
